import { useMediaQuery,useTheme } from '@mui/material'
import { DialogContent,Drawer, Modal } from '@mui/material'
import { TokenInfo } from '@renec-foundation/rpl-token-registry'
import { useRouter } from 'next/router'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import usePagination, { type UsePaginationParams } from '@/hooks/use-pagination'
import { useSettings } from '@/hooks/use-settings'

import { searchTokenFromList } from '../TokenPicker/utils'
import TokenPickerRevampProvider, { TokenPickerRevampContext } from './context'
import TokenList from './TokenList'
import { TokenListHighlightMessages, TokenPickerProps } from './types'
import View from './view'

const dialogClasses = { root: 'max-w-[488px] sm:min-w-[488px] p-0 mx-auto outline-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2' }

export const Container = ({
  selectedToken: initSelectedToken,
  options: tokens,
  dataTestId,
  onChangeToken,
  nickName,
  slot,
  isDisplayActionView = true,
  isDisplayHighlightMessage = true,
}: TokenPickerProps) => {
  const theme = useTheme()
  const { locale = 'en' } = useRouter()
  const { data: settingsData } = useSettings()
  const onChangeTokenRef = useRef(onChangeToken)
  onChangeTokenRef.current = onChangeToken // No need to care whether the outside uses memo or not
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))
  const { isOpenPicker, setIsOpenPicker } = useContext(TokenPickerRevampContext)
  const [searchStr, setSearchStr] = useState('')
  const [selectedToken, setSelectedToken] = useState<TokenPickerProps['selectedToken']>(initSelectedToken)

  const paginationParams = useMemo<UsePaginationParams<TokenInfo>>(
    () => ({
      queryKey: `tokens?keyword=${searchStr.trim().toLowerCase()}`,
      loader: () => Promise.resolve([]),
      comparator: (x, y) => x.address === y.address,
      initialData: tokens,
      itemsPerPage: 8,
    }),
    [searchStr, tokens],
  )

  const {
    hasMoreItems: hasMoreTokens,
    items: options,
    loadMore: loadMoreTokens,
    setItems: setAllTokens,
  } = usePagination(paginationParams)

  useEffect(() => {
    const searchingOptions = !searchStr ? tokens : searchTokenFromList(searchStr, options)
    setAllTokens(searchingOptions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens, searchStr])

  useEffect(() => {
    setSelectedToken(initSelectedToken)
  }, [initSelectedToken])

  const handleToggleOpen = useCallback(() => {
    setIsOpenPicker(o => !o)
    setSearchStr('')
  }, [setIsOpenPicker])

  const handleClickTokenItem = useCallback((token: TokenInfo) => {
    setSelectedToken(token)
    onChangeTokenRef.current(token)
    setIsOpenPicker(false)
  }, [onChangeTokenRef, setIsOpenPicker])

  const highlightMessages = useMemo(() => {
    const emptyValue = {
      url: '',
      markdown: '',
    }
    if (!isDisplayHighlightMessage) return emptyValue
    const returnedValue = settingsData?.highlight_messages?.[locale] || emptyValue
    return returnedValue as TokenListHighlightMessages
  }, [settingsData, locale, isDisplayHighlightMessage])

  const injectedProps = {
    nickName,
    options,
    search: searchStr,
    onSearch: setSearchStr,
    onClickTokenItem: handleClickTokenItem,
    hasMoreTokens,
    loadMoreTokens,
    onClickClose: handleToggleOpen,
    highlightMessages,
    slot,
  }

  return (
    <>
      {
        isDisplayActionView && (
          <View
            dataTestId={dataTestId}
            token={selectedToken}
            onClick={handleToggleOpen}
          />
        )
      }
      {
        isDesktop ? (
          <Modal id="token-picker" open={isOpenPicker} onClose={handleToggleOpen}>
            <DialogContent classes={dialogClasses}>
              <TokenList {...injectedProps} maxHeight="350px" />
            </DialogContent>
          </Modal>
        ): (
          <Drawer
            id="token-picker"
            open={isOpenPicker}
            anchor="bottom"
            onClose={handleToggleOpen}
            BackdropProps={{ className: 'token-picker-backdrop' }}
            sx={{
              '.MuiPaper-elevation': {
                backgroundColor: 'transparent',
                backgroundImage: 'none',
              },
            }}
          >
            <TokenList
              {...injectedProps}
              height={highlightMessages.markdown ? '50vh' : '40vh'}
            />
          </Drawer>
        )
      }
    </>
  )
}

const ContainerWithProvider = (props: TokenPickerProps) => {
  return <TokenPickerRevampProvider><Container {...props} /></TokenPickerRevampProvider>
}

export default memo(ContainerWithProvider)
